import React, { useEffect, useMemo, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import PassMappingPanel from './PassMappingPanel'
import type { BatchWeldDraft } from '../../../utils/alx/alxUploadPipeline'
import { countLearnedPrefills } from '../../../utils/weldUpload/passMappingAutofill'

export interface AlxBatchPassMappingAccordionProps {
  drafts: BatchWeldDraft[]
  wpsPassLabels: string[]
  onDraftMappingsChange: (rowId: string, passMappings: Record<string, string>) => void
  disabled?: boolean
  passSourceLabel?: string
}

function draftPassNames(draft: BatchWeldDraft): string[] {
  if (draft.distinctPassNames && draft.distinctPassNames.length > 0) {
    return [...draft.distinctPassNames].sort()
  }
  return Array.from(new Set(draft.rows.map((r) => r.passName))).sort()
}

function isDraftFullyMapped(draft: BatchWeldDraft): boolean {
  const names = draftPassNames(draft)
  return names.length > 0 && names.every((n) => (draft.passMappings[n] ?? '').trim() !== '')
}

export default function AlxBatchPassMappingAccordion({
  drafts,
  wpsPassLabels,
  onDraftMappingsChange,
  disabled = false,
  passSourceLabel = 'ALX pass'
}: AlxBatchPassMappingAccordionProps) {
  const sorted = useMemo(
    () => [...drafts].sort((a, b) => a.weldIdLabel.localeCompare(b.weldIdLabel)),
    [drafts]
  )
  const [expandedId, setExpandedId] = useState<string | null>(sorted[0]?.rowId ?? null)

  useEffect(() => {
    if (sorted.length === 0) {
      setExpandedId(null)
      return
    }
    if (!expandedId || !sorted.some((d) => d.rowId === expandedId)) {
      setExpandedId(sorted[0].rowId)
    }
  }, [sorted, expandedId])

  function handleMappingChange(rowId: string, rawName: string, canonical: string) {
    const draft = drafts.find((d) => d.rowId === rowId)
    if (!draft) return
    const next = { ...draft.passMappings, [rawName]: canonical }
    onDraftMappingsChange(rowId, next)
    if (isDraftFullyMapped({ ...draft, passMappings: next })) {
      const idx = sorted.findIndex((d) => d.rowId === rowId)
      const nextIncomplete = sorted.slice(idx + 1).find((d) => !isDraftFullyMapped(d))
      if (nextIncomplete) setExpandedId(nextIncomplete.rowId)
    }
  }

  if (sorted.length === 0) return null

  return (
    <div className="space-y-2" data-testid="alx-batch-pass-accordion">
      {sorted.map((draft) => {
        const rawPassNames = draftPassNames(draft)
        const mappedCount = rawPassNames.filter((p) => (draft.passMappings[p] ?? '') !== '').length
        const learnedPrefillCount = countLearnedPrefills(
          rawPassNames,
          draft.passMappings,
          draft.learnedSuggestions ?? {},
          wpsPassLabels
        )
        const expanded = expandedId === draft.rowId
        const complete = mappedCount === rawPassNames.length && rawPassNames.length > 0

        return (
          <div
            key={draft.rowId}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
            data-testid={`alx-batch-accordion-${draft.weldIdLabel}`}
          >
            <button
              type="button"
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
              aria-expanded={expanded}
              onClick={() => setExpandedId(expanded ? null : draft.rowId)}
            >
              <ChevronDownIcon
                className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform ${
                  expanded ? 'rotate-180' : ''
                }`}
              />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Weld ID
                </p>
                <p className="truncate text-sm font-semibold text-gray-900">{draft.weldIdLabel}</p>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
                  complete
                    ? 'bg-success-50 text-success-700 ring-success-200'
                    : 'bg-warning-50 text-warning-700 ring-warning-200'
                }`}
              >
                {mappedCount}/{rawPassNames.length} mapped
              </span>
            </button>
            {expanded ? (
              <div className="border-t border-gray-100 px-2 pb-2">
                <PassMappingPanel
                  compact
                  rawPassNames={rawPassNames}
                  passMappings={draft.passMappings}
                  wpsPassLabels={wpsPassLabels}
                  learnedPrefillCount={learnedPrefillCount}
                  onMappingChange={(raw, canonical) =>
                    handleMappingChange(draft.rowId, raw, canonical)
                  }
                  disabled={disabled}
                  sourceLabel={passSourceLabel}
                />
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
